'use client';

import { PrimaryNavItem } from '../types';
import Image from '@/components/Image';
import Tabs from '@/components/Tabs';
import { Box } from '@mui/material';
import type { ReactNode } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { EPlatform } from '@/utils/enums/platform-enum';

interface NavigationSectionProps {
  isWeb: boolean;
  deviceType?: EPlatform;
  navigation: {
    wap: PrimaryNavItem[];
    web: PrimaryNavItem[];
  };
  onStickyNavChange?: (visible: boolean) => void;
  /** PC: sticky nav 是否可见（由父组件控制） */
  stickyNavVisible?: boolean;
  /** PC: 当前一级导航索引 */
  primaryIndex?: number;
  /** PC: 每个一级导航下选中的二级索引 */
  pcSelectedSecondary?: Record<number, number | null>;
  /** PC: 点击一级导航 */
  onPrimaryClick?: (index: number) => void;
  /** PC: 点击二级导航（再次点击取消） */
  onSecondaryToggle?: (primaryIndex: number, secondaryIndex: number) => void;
  /** PC: 选中二级导航的 banner 图 */
  selectedBannerImage?: string;
  /** 自定义 PC 首屏 banner 渲染，未传时保持原有 Image 渲染 */
  renderHeroBanner?: (banner: { image?: string; alt?: string }) => ReactNode;
  /** M 端：当前二级导航索引 */
  secondaryIndex?: number;
  /** M 端：点击二级导航 */
  onSecondaryClick?: (index: number) => void;
}

export default function NavigationSection({
  isWeb,
  deviceType,
  navigation,
  onStickyNavChange,
  stickyNavVisible = false,
  primaryIndex = 0,
  pcSelectedSecondary = {},
  onPrimaryClick,
  onSecondaryToggle,
  selectedBannerImage,
  renderHeroBanner,
  secondaryIndex = -1,
  onSecondaryClick,
}: NavigationSectionProps) {
  const isApp = [EPlatform.ANDROID, EPlatform.IOS].includes(deviceType as EPlatform);
  const bannerRef = useRef<HTMLDivElement>(null);
  const stickyNavRef = useRef<HTMLDivElement>(null);
  const mobileSecondaryRef = useRef<HTMLDivElement>(null);
  const mobileStickySecondaryRef = useRef<HTMLDivElement>(null);

  // 选中二级导航时自动滚动到可视区域（PC 吸顶导航 + M 端主/吸顶导航）
  useEffect(() => {
    const containers = isWeb
      ? [stickyNavRef.current]
      : [mobileSecondaryRef.current, mobileStickySecondaryRef.current];
    containers.forEach((container) => {
      if (!container) return;
      const activeItem = container.querySelector(
        '[data-secondary-active="true"]',
      ) as HTMLElement | null;
      if (activeItem) {
        activeItem.scrollIntoView({ behavior: 'smooth', inline: 'nearest', block: 'nearest' });
      }
    });
  }, [secondaryIndex, primaryIndex, isWeb, pcSelectedSecondary]);

  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const bannerVisible = useRef(true);
  const lastScrollY = useRef(0);
  const anchorScrollY = useRef(0);
  const scrollLockRef = useRef(false);
  const onStickyNavChangeRef = useRef(onStickyNavChange);
  onStickyNavChangeRef.current = onStickyNavChange;

  const SCROLL_THRESHOLD = 4;

  const checkScroll = useCallback(() => {
    const el = stickyNavRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > SCROLL_THRESHOLD);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - SCROLL_THRESHOLD);
  }, []);

  const handleScrollArrow = useCallback((direction: 'left' | 'right') => {
    const el = stickyNavRef.current;
    if (!el) return;
    el.scrollBy({ left: direction === 'left' ? -200 : 200, behavior: 'smooth' });
  }, []);

  const updateNavShow = useCallback(
    (value: boolean) => {
      onStickyNavChangeRef.current?.(value);
      if (value) {
        requestAnimationFrame(checkScroll);
      }
    },
    [checkScroll],
  );

  useEffect(() => {
    const banner = bannerRef.current;
    if (!banner) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        bannerVisible.current = entry.isIntersecting;
        if (entry.isIntersecting) {
          updateNavShow(false);
        }
      },
      { threshold: 0 },
    );
    observer.observe(banner);

    const handleScroll = () => {
      if (scrollLockRef.current) return;
      const currentY = window.scrollY;
      if (bannerVisible.current) {
        lastScrollY.current = currentY;
        anchorScrollY.current = currentY;
        return;
      }
      const scrollingDown = currentY > lastScrollY.current;
      const delta = Math.abs(currentY - anchorScrollY.current);
      if (delta >= 64) {
        updateNavShow(!scrollingDown);
        anchorScrollY.current = currentY;
        // Lock during CSS transition to prevent height-change feedback loop
        scrollLockRef.current = true;
        setTimeout(() => {
          scrollLockRef.current = false;
          lastScrollY.current = window.scrollY;
          anchorScrollY.current = window.scrollY;
        }, 350);
      }
      lastScrollY.current = currentY;
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', handleScroll);
    };
  }, [updateNavShow]);

  // M 端：一级导航切换时重置二级滚动到最左侧
  useEffect(() => {
    if (isWeb) return;
    const el = mobileSecondaryRef.current;
    if (el) el.scrollLeft = 0;
  }, [primaryIndex, isWeb]);

  if (isWeb) {
    const webNav = navigation.web[primaryIndex];
    const selectedIdx = pcSelectedSecondary[primaryIndex];
    const currentSecondaryItem =
      selectedIdx != null
        ? webNav?.secondaryNav[selectedIdx]
        : webNav?.secondaryNav.find((s) => s.isDefault) || webNav?.secondaryNav[0];
    const bannerTitleColor = currentSecondaryItem?.titleColor || '#282828';
    // 二级导航无 banner 时回退到一级导航的 banner
    const heroBannerImage = currentSecondaryItem?.bannerImage || webNav?.bannerImage;

    return (
      <>
        <div ref={bannerRef} className="relative w-full">
          {renderHeroBanner ? (
            renderHeroBanner({ image: heroBannerImage, alt: webNav?.label })
          ) : (
            <div className="w-full lg:min-h-[358px] min-h-[398px] overflow-hidden aspect-[1920/560]">
              <Image
                className="w-full lg:min-h-[358px] min-h-[398px] overflow-hidden object-cover aspect-[1920/560]"
                src={heroBannerImage}
                alt={webNav?.label}
                width={1920}
                height={560}
              />
            </div>
          )}
          <div className="absolute 2xl:bottom-[1.042vw] bottom-[10px] lg:top-[auto] top-[10px] lg:left-[0] lg:right-[0] 2xl:px-[2.5vw] xl:px-[1.903vw] lg:px-[1.695vw] pl-[0] left-[20px] flex lg:flex-row flex-col justify-center 2xl:gap-[1.562vw] xl:gap-[1.171vw] lg:gap-[0.678vw] gap-[8px] w-[428px] lg:w-full">
            {navigation.web.map((item, pItemIdx) => (
              <div
                key={pItemIdx}
                className="relative lg:flex-1 lg:max-w-[33.3333%] max-w-[428px] rounded-[12px] bg-gradient-to-b from-[rgba(255,255,255,0.55)] to-[rgba(255,255,255,0.32)] backdrop-blur-[20px] backdrop-saturate-[160%] border-[1px] border-[rgba(255,255,255,0.6)] shadow-[inset_0_1px_0_0_rgba(255,255,255,0.9),0_8px_24px_-4px_rgba(40,40,40,0.08)] 2xl:pt-[1.146vw] lg:pt-[16px] 2xl:px-[1.25vw] xl:px-[14px] p-[14px_14px_10px_14px] 2xl:pb-[1.042vw] xl:pb-[16px] flex flex-col items-start justify-start 2xl:gap-[0.729vw] gap-[8px]"
              >
                <div className="flex-1 flex items-center justify-start">
                  <h2 className="relative 2xl:text-[22px] 3xl:text-[1.146vw] text-[16px] font-bb text-[#282828] leading-[1] line-clamp-2 text-ellipsis">
                    {item.label}
                  </h2>
                </div>
                <div className="relative flex-1 flex items-center justify-start 2xl:gap-[0.625vw] gap-[8px] w-full">
                  {item.secondaryNav.map((subItem, sItemIdx) => {
                    if (subItem.hidden) return null;
                    const selectedSecondaryIndex = pcSelectedSecondary[pItemIdx];
                    const isSelected =
                      selectedSecondaryIndex === sItemIdx ||
                      (selectedSecondaryIndex == null && subItem.isDefault);
                    return (
                      <div
                        key={sItemIdx}
                        className={`relative lg:aspect-[172/100] aspect-[128/70] flex-1 box-content bg-[#ffffff] rounded-[6px] overflow-hidden cursor-pointer border-[2px] ${isSelected ? 'border-[#282828]' : 'border-transparent'}`}
                        onClick={() => onSecondaryToggle?.(pItemIdx, sItemIdx)}
                      >
                        {subItem.labelBg && (
                          <Image
                            className="w-full h-full object-contain"
                            src={subItem.labelBg}
                            alt={subItem.label}
                            width={172}
                            height={100}
                          />
                        )}
                        <div
                          className={`absolute 2xl:bottom-[4px] bottom-[2px] 2xl:left-[6px] xl:left-[4px] left-[2px] 2xl:right-[6px] xl:right-[4px] right-[2px] 2xl:h-[32px] xl:h-[28px] h-[24px] flex items-center justify-center`}
                        >
                          <p
                            className={`2xl:text-[16px] 3xl:text-[16px] text-[12px] text-[#282828] leading-[1] line-clamp-2 text-ellipsis text-center ${isSelected ? 'font-bb' : 'font-br'}`}
                          >
                            {subItem.label}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
        <div
          className={`w-full overflow-hidden sticky ${isApp ? 'top-0' : 'top-[100px]'} bg-[#ffffff] z-100 transition-[height] duration-300 ease-in-out ${stickyNavVisible ? '2xl:h-[64px] h-[104px]' : 'h-0'}`}
        >
          <div className="w-[calc(100% - 98px)] box-border ml-[48px] mr-[40px] border-b-[1px] border-b-[#eeeeee] 2xl:h-[64px] h-[104px]">
            <div className="relative w-full h-full flex items-center justify-start">
              {canScrollLeft && (
                <button
                  className="absolute left-[-24px] top-0 bottom-0 w-[24px] z-[1] flex items-center justify-center text-[#646464] hover:text-[#282828] cursor-pointer transition-all duration-300"
                  onClick={() => handleScrollArrow('left')}
                >
                  <svg width="8" height="12" viewBox="0 0 8 12" fill="none">
                    <path
                      d="M7 1L2 6L7 11"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              )}
              <div
                ref={stickyNavRef}
                className={`2xl:h-[32px] h-[62px] flex items-center overflow-x-auto overflow-y-hidden scrollbar-hidden ${canScrollLeft ? 'pl-[48px]' : ''} ${canScrollRight ? 'pr-[48px]' : ''}`}
                onScroll={checkScroll}
              >
                {navigation.web.map((item, pItemIdx) => (
                  <div
                    key={pItemIdx}
                    className={`h-full flex 2xl:flex-row flex-col 2xl:items-center items-start 2xl:justify-center justify-start shrink-0 ${pItemIdx > 0 ? 'pl-[24px]' : ''} ${pItemIdx < navigation.web.length - 1 ? 'pr-[24px] border-r-[1px] border-r-[#eeeeee]' : ''}`}
                  >
                    <p className="text-[16px] font-bb text-[#282828] text-left leading-[19px] 2xl:mr-[14px] mr-[0] 2xl:mb-[0] mb-[10px] whitespace-nowrap">
                      {item.label}
                    </p>
                    <div className="flex 2xl:items-center gap-[12px]">
                      {item.secondaryNav.map((subItem, sItemIdx) => {
                        if (subItem.hidden) return null;
                        const selectedSecondaryIndex = pcSelectedSecondary[pItemIdx];
                        const isSelected =
                          selectedSecondaryIndex === sItemIdx ||
                          (selectedSecondaryIndex == null && subItem.isDefault);
                        return (
                          <div
                            key={sItemIdx}
                            className={`px-[16px] h-[32px] leading-[32px] border-[1px] rounded-[16px] cursor-pointer whitespace-nowrap transition-all duration-200 box-border ${isSelected ? 'bg-[#282828] text-white border-[#282828]' : 'border-[#282828] text-[#282828]'}`}
                            data-secondary-active={isSelected || undefined}
                            onClick={() => onSecondaryToggle?.(pItemIdx, sItemIdx)}
                          >
                            {subItem.label}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
              {canScrollRight && (
                <div
                  className="absolute right-[-24px] top-0 bottom-0 w-[24px] z-[1] flex items-center justify-center text-[#646464] hover:text-[#282828] cursor-pointer"
                  onClick={() => handleScrollArrow('right')}
                >
                  <svg width="8" height="12" viewBox="0 0 8 12" fill="none">
                    <path
                      d="M1 1L6 6L1 11"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
              )}
            </div>
          </div>
        </div>
      </>
    );
  }

  const currentPrimary = navigation.wap[primaryIndex];

  // M 端：判断当前一级导航下的二级导航是否全部有图（包含默认导航位，排除 hidden 项）
  // 一级导航配置了 Banner 图时，二级导航不展示图片，走文字标签模式
  const visibleSecondaryNav = currentPrimary?.secondaryNav?.filter((s) => !s.hidden) || [];
  const allSecondaryHaveImage =
    !currentPrimary?.bannerImage && visibleSecondaryNav.every((s) => !!s.bannerImage);

  return (
    <>
      <div ref={bannerRef} className="w-full">
        {/* 一级导航 Tabs */}
        <Box
          sx={{
            backgroundColor: '#ffffff',
            '& .MuiTabs-list': {
              padding: '0 14px',
              gap: '26px',
              '.MuiButtonBase-root': {
                padding: '18px 0 7.5px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: '80vw',
                display: 'block',
              },
            },
          }}
        >
          <Tabs
            items={navigation.wap.map((item, index) => ({
              key: String(index),
              label: item.label,
            }))}
            activeKey={String(primaryIndex)}
            onChange={(key) => onPrimaryClick?.(Number(key))}
            scrollable
            scrollButtons="auto"
            hasTabContent={false}
            showLine={false}
          />
        </Box>

        {/* 一级导航 Banner 图 */}
        {currentPrimary?.hasImage && currentPrimary?.bannerImage && (
          <div className="w-full">
            <Image
              className="w-full object-cover"
              src={currentPrimary.bannerImage}
              alt={currentPrimary.label}
              width={750}
              height={200}
            />
          </div>
        )}

        {/* 二级导航 */}
        <div className="w-full relative">
          <div
            ref={mobileSecondaryRef}
            className={`flex items-center gap-[8px] overflow-x-auto scrollbar-hidden px-[14px] ${allSecondaryHaveImage ? 'h-[190px] border-b-[1px] border-b-[#eeeeee] py-[13px]' : 'py-[12px]'}`}
          >
            {currentPrimary?.secondaryNav?.map((subItem, sIdx) => {
              if (subItem.hidden) return null;
              const isSelected =
                secondaryIndex === sIdx || (secondaryIndex < 0 && subItem.isDefault);

              {
                /* 图片卡片模式：全部二级导航都配了图片时 */
              }
              if (allSecondaryHaveImage) {
                return (
                  <div
                    key={sIdx}
                    className={`relative rounded-[10px] overflow-hidden cursor-pointer border-[1px] transition-[height,border-color] duration-300 ${isSelected ? 'border-[#282828] h-[164px]' : 'border-transparent h-[137.5px]'} ${currentPrimary?.secondaryNav?.length > 2 ? 'aspect-[246/328]' : 'aspect-[368/328]'}`}
                    onClick={() => onSecondaryClick?.(sIdx)}
                  >
                    <Image
                      className={`w-full h-full object-cover`}
                      src={subItem.bannerImage!}
                      alt={subItem.label}
                      width={328}
                      height={288}
                    />
                    <div
                      className={`absolute bottom-0 left-0 right-0 h-[18px] text-center text-[10px] ${isSelected ? 'bg-[#282828]' : 'bg-[rgba(40,40,40,0.6)] backdrop-blur-md'}`}
                    >
                      <div
                        className={`leading-[18px] w-full px-[8px] text-[#ffffff] text-center line-clamp-1 ${isSelected ? 'font-bb' : 'font-br'}`}
                      >
                        {subItem.label}
                      </div>
                    </div>
                  </div>
                );
              }

              {
                /* 文字标签模式 */
              }
              return (
                <div
                  key={sIdx}
                  className={`px-[10px] h-[28px] leading-[28px] border-[1px] rounded-[14px] cursor-pointer whitespace-nowrap shrink-0 text-[11px] text-center box-border overflow-y-hidden max-w-[95vw] min-w-[43px] text-ellipsis overflow-hidden ${isSelected ? 'bg-[#282828] text-white border-[#282828]' : 'border-[#282828] text-[#282828]'}`}
                  data-secondary-active={isSelected || undefined}
                  onClick={() => onSecondaryClick?.(sIdx)}
                >
                  {subItem.label}
                </div>
              );
            })}
          </div>
        </div>
      </div>
      <div
        className={`w-full overflow-hidden sticky ${isApp ? 'top-0' : 'top-[11.733vw]'} bg-[#ffffff] z-[100] transition-[height] duration-300 ease-in-out ${stickyNavVisible ? 'h-[25.6vw] border-b-[1px] border-b-[#eeeeee]' : 'h-0'}`}
      >
        {/* 一级导航 Tabs */}
        <Box
          sx={{
            backgroundColor: '#ffffff',
            '& .MuiTabs-list': {
              padding: '0 14px',
              gap: '26px',
              '.MuiButtonBase-root': {
                padding: '18px 0 7.5px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: '80vw',
                display: 'block',
              },
            },
          }}
        >
          <Tabs
            items={navigation.wap.map((item, index) => ({
              key: String(index),
              label: item.label,
            }))}
            activeKey={String(primaryIndex)}
            onChange={(key) => onPrimaryClick?.(Number(key))}
            scrollable
            scrollButtons="auto"
            hasTabContent={false}
            showLine={false}
          />
        </Box>

        {/* 二级导航 */}
        <div className="w-full relative">
          <div
            ref={mobileStickySecondaryRef}
            className="flex gap-[8px] py-[12px] px-[14px] overflow-x-auto scrollbar-hidden"
          >
            {currentPrimary?.secondaryNav?.map((subItem, sIdx) => {
              if (subItem.hidden) return null;
              const isSelected =
                secondaryIndex === sIdx || (secondaryIndex < 0 && subItem.isDefault);
              return (
                <div
                  key={sIdx}
                  className={`px-[10px] h-[28px] leading-[28px] border-[1px] rounded-[14px] cursor-pointer whitespace-nowrap shrink-0 text-[11px] text-center box-border overflow-y-hidden max-w-[95vw] min-w-[43px] text-ellipsis overflow-hidden ${isSelected ? 'bg-[#282828] text-white border-[#282828]' : 'border-[#282828] text-[#282828]'}`}
                  data-secondary-active={isSelected || undefined}
                  onClick={() => onSecondaryClick?.(sIdx)}
                >
                  {subItem.label}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </>
  );
}
